

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
    Autocomplete,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewCustomerCouponModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        coupon_id: null,
        customer_id: null,
        can_be_used_partially: false,
        amount: '',
        expiry_date: '',
        available_amount: '',
        currency: 'GBP'
    });

    const [formErrors, setFormErrors] = useState({});
    const [coupons, setCoupons] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loadingCoupons, setLoadingCoupons] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchCoupons();
            fetchCustomers();
        }
    }, [isOpen]);

    const fetchCoupons = async () => {
        setLoadingCoupons(true);
        try {
            const response = await axiosInstance.get('/coupons/');
            setCoupons(response.data || []);
        } catch (error) {
            console.error('Error fetching coupons:', error);
        } finally {
            setLoadingCoupons(false);
        }
    };

    const fetchCustomers = async () => {
        setLoadingCustomers(true);
        try {
            const response = await axiosInstance.get('/customers/');
            setCustomers(response.data || []);
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setLoadingCustomers(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.coupon_id) errors.coupon_id = 'Coupon is required';
        if (!formData.customer_id) errors.customer_id = 'Customer is required';
        if (!formData.amount) errors.amount = 'Amount is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                amount: parseFloat(formData.amount),
                available_amount: formData.available_amount ? parseFloat(formData.available_amount) : null,
                expiry_date: formData.expiry_date || null
            };
            await onSave(payload);
            setFormData({
                name: '',
                coupon_id: null,
                customer_id: null,
                can_be_used_partially: false,
                amount: '',
                expiry_date: '',
                available_amount: '',
                currency: 'GBP'
            });
            onClose();
        } catch (error) {
            console.error('Error saving customer coupon:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving customer coupon',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '80%', md: '700px' },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <MDBox className="pearl-popup-header"
                        sx={{
                            backgroundColor: colors.popupHeader.background,
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8
                        }}
                    >
                        <MDTypography variant="h6" sx={{ color: '#FFFFFF' }}>
                            New Customer Coupon
                        </MDTypography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon sx={{ color: '#FFFFFF' }} />
                        </IconButton>
                    </MDBox>
                    <MDBox p={3}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={coupons}
                                        getOptionLabel={(option) => option?.name || ''}
                                        loading={loadingCoupons}
                                        value={coupons.find(c => c.id === formData.coupon_id) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, coupon_id: newValue?.id || null }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Coupon"
                                                variant="outlined"
                                                margin="normal"
                                                required
                                                error={!!formErrors.coupon_id}
                                                helperText={formErrors.coupon_id}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingCoupons ? <CircularProgress size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={customers}
                                        getOptionLabel={(option) => option?.name || ''}
                                        loading={loadingCustomers}
                                        value={customers.find(c => c.id === formData.customer_id) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, customer_id: newValue?.id || null }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Customer"
                                                variant="outlined"
                                                margin="normal"
                                                required
                                                error={!!formErrors.customer_id}
                                                helperText={formErrors.customer_id}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingCustomers ? <CircularProgress size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Amount"
                                        name="amount"
                                        type="number"
                                        required
                                        value={formData.amount}
                                        onChange={handleInputChange}
                                        error={!!formErrors.amount}
                                        helperText={formErrors.amount}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Available Amount"
                                        name="available_amount"
                                        type="number"
                                        value={formData.available_amount}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Currency</InputLabel>
                                        <Select
                                            name="currency"
                                            value={formData.currency}
                                            onChange={handleInputChange}
                                            label="Currency"

                                        >
                                            <MenuItem value="GBP">GBP</MenuItem>
                                            <MenuItem value="AUD">AUD</MenuItem>
                                            <MenuItem value="USD">USD</MenuItem>
                                            <MenuItem value="EUR">EUR</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Expiry Date"
                                        name="expiry_date"
                                        type="datetime-local"
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.expiry_date}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <MDBox mt={1}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formData.can_be_used_partially}
                                                    onChange={handleInputChange}
                                                    name="can_be_used_partially"
                                                />
                                            }
                                            label="Partial Use Allowed"
                                        />
                                    </MDBox>
                                </Grid>
                            </Grid>
                            <MDBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
                                <MDButton variant="outlined" color="secondary" onClick={onClose}>
                                    Cancel
                                </MDButton>
                                <MDButton variant="gradient" color="info" type="submit" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={24} /> : 'Save'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </MDBox>
                </Box>
            </Modal>
            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity}>{toast.message}</Alert>
            </Snackbar>
        </>
    );
};

export default NewCustomerCouponModal;

